import { Alert, Button, Group, Stack, Title } from "@mantine/core";
import { Link, Navigate } from "react-router-dom";

import { RequirePermission } from "@/auth/guards";
import { useIAMAuth } from "@/auth/iam";
import { useResolvedPermissions } from "@/auth/permissions";
import { config } from "@/config";
import { AppLayout } from "@/components/AppLayout";
import { t } from "@/i18n";

/**
 * Home page — the authenticated landing surface. Real navigation lives in the
 * shared `<AppLayout>` (header + sign-out); this is the entry point into the
 * catalog read/authoring surfaces.
 *
 * **Every button is gated on exactly what its route requires.** Until 2026-08-09 the
 * five catalog buttons were ungated while their routes all require `catalog:read`, so a
 * user without it saw a full set of buttons that each led to a not-found page. That is
 * how it presented when the portal was first opened against real IAM: signed in, links
 * visible, everything "broken". The links are the honest signal of what you can reach —
 * if a route grows a different requirement, gate its button with the same one.
 */
export function HomePage(): JSX.Element {
  const { isReady, isError, has } = useResolvedPermissions();
  // docs#239 item 6: Medicus staff carry no organisation claim. A lab user with
  // audit:read reviews her own lab on /portal/audit, not the staff "all tenants" page.
  const { organizationId } = useIAMAuth();
  const isStaff = organizationId === null || organizationId === "";

  const canRead = has("catalog:read");
  const canManageClient = has("clients:manage");
  // A lab doctor may hold the clinical read and nothing else. With the Reports page on,
  // that is access, so they must not be told they have none.
  const canReadReports = config.portalReportsEnabled && has("reports:clinical:read");
  // Gate B white-labelling: staff branding, a client admin's branding and a doctor's own
  // signature are access too, with the branding routes switched on.
  const canBrand =
    config.brandingEnabled &&
    (has("branding:admin") || has("branding:manage") || has("branding:signature"));
  const nothingVisible =
    isReady &&
    !isError &&
    !canRead &&
    !has("audit:read") &&
    !canManageClient &&
    !canReadReports &&
    !canBrand;
  // docs#15 note 16595: a doctor whose account reads reports while the Reports page is off
  // was told they had no permissions. That is a different cause with a different owner.
  const reportsSwitchedOff =
    nothingVisible && !config.portalReportsEnabled && has("reports:clinical:read");
  // docs#15 note 16595 item 2: a user whose only access is the Reports page lands on the
  // list itself, not on a page holding one button. A doctor's own signature (branding
  // signature) does not count as other access, the list links to it.
  const onlyReports =
    isReady &&
    !isError &&
    canReadReports &&
    !canRead &&
    !has("audit:read") &&
    !canManageClient &&
    !(config.brandingEnabled && (has("branding:admin") || has("branding:manage")));
  if (onlyReports) {
    return <Navigate to="/portal/reports" replace />;
  }

  return (
    <AppLayout>
      <Title order={1}>{t("home.welcome")}</Title>

      <Stack gap="md" mt="md">
        <Group>
          {/* All five need `catalog:read` (see App.tsx). */}
          <RequirePermission perm="catalog:read">
            <Button component={Link} to="/catalogs">
              {t("home.view_catalogs")}
            </Button>
          </RequirePermission>
          <RequirePermission perm="catalog:read">
            <Button component={Link} to="/authoring/search" variant="light">
              {t("home.search_authoring")}
            </Button>
          </RequirePermission>
          <RequirePermission perm="catalog:read">
            <Button component={Link} to="/authoring/rules" variant="light">
              {t("home.view_rules")}
            </Button>
          </RequirePermission>
          <RequirePermission perm="catalog:read">
            <Button component={Link} to="/authoring/content" variant="light">
              {t("home.view_content")}
            </Button>
          </RequirePermission>
          <RequirePermission perm="catalog:read">
            <Button component={Link} to="/authoring/simulate" variant="light">
              {t("home.open_simulator")}
            </Button>
          </RequirePermission>
          {/* The audit viewers were reachable only by typing the URL. The X1 runbook
              has clinical reviewers opening this weekly, and nothing linked to it —
              two people who knew the codebase still went to the wrong one first. */}
          <RequirePermission perm="audit:read">
            <Button
              component={Link}
              to={isStaff ? "/admin/audit" : "/portal/audit"}
              variant="light"
            >
              {t("home.review_audit")}
            </Button>
          </RequirePermission>
          {/* The portal link follows the server-resolved clients:manage, which is what
              every portal route's backend enforces. It used to read the token's `role`
              claim, which real IAM does not set for an invited portal user, so real
              admins never saw this link. The tenant claim is deliberately NOT checked
              here: an admin whose token lacks it should land on the route guard's
              "tenant misconfigured" page, not be told they have no access. */}
          <RequirePermission perm="clients:manage">
            <Button component={Link} to="/portal" variant="light">
              {t("home.open_portal")}
            </Button>
          </RequirePermission>
          {/* Gate B M1 S7. Shown only with the Reports page switched on, and on the
              permission its route and BFF check. The tenant claim is not checked here,
              for the same reason as the portal link above. */}
          {config.portalReportsEnabled ? (
            <RequirePermission perm="reports:clinical:read">
              <Button component={Link} to="/portal/reports" variant="light">
                {t("home.open_reports")}
              </Button>
            </RequirePermission>
          ) : null}
          {/* Gate B white-labelling, each on the key its route checks. The tenant claim is
              not checked here, for the same reason as the portal link above. */}
          {config.brandingEnabled ? (
            <RequirePermission perm="branding:admin">
              <Button component={Link} to="/admin/branding" variant="light">
                {t("home.open_branding_admin")}
              </Button>
            </RequirePermission>
          ) : null}
          {config.brandingEnabled ? (
            <RequirePermission perm="branding:manage">
              <Button component={Link} to="/portal/branding" variant="light">
                {t("home.open_branding")}
              </Button>
            </RequirePermission>
          ) : null}
          {config.brandingEnabled ? (
            <RequirePermission perm="branding:signature">
              <Button component={Link} to="/portal/signature" variant="light">
                {t("home.open_signature")}
              </Button>
            </RequirePermission>
          ) : null}
        </Group>

        {/* Hiding the buttons is only half the fix. A signed-in user staring at a page
            with nothing on it cannot tell "no access" from "app is broken" — the same
            reads-as-absence trap the audit viewer's empty list exists to avoid. Say which
            it is. The two cases are kept apart deliberately: a FAILED resolve must not be
            reported as "you have no access", because we do not know that. */}
        {isError && (
          <Alert color="yellow" title={t("home.permissions_unavailable_title")}>
            {t("home.permissions_unavailable_body")}
          </Alert>
        )}
        {reportsSwitchedOff && (
          <Alert color="blue" title={t("home.reports_off_title")} data-testid="home-reports-off">
            {t("home.reports_off_body")}
          </Alert>
        )}
        {nothingVisible && !reportsSwitchedOff && (
          <Alert color="blue" title={t("home.no_access_title")}>
            {t("home.no_access_body")}
          </Alert>
        )}
      </Stack>
    </AppLayout>
  );
}
